import type { SVGProps } from 'react'

/**
 * The design's SVG icon set (ICONS map in axis-group-mobility.design.html),
 * ported verbatim as typed React components. Stroke-based 24×24 icons that
 * inherit `currentColor`; by default they fill their parent (width/height
 * 100%), so size them with a wrapper (e.g. `<span className="size-[18px]">`)
 * exactly like the design does — or pass explicit width/height/className.
 * All are decorative by default (aria-hidden); pass aria-* props to override.
 */
export type IconProps = SVGProps<SVGSVGElement>

const strokeDefaults = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const

export function Home(props: IconProps) {
  return (
    <svg {...strokeDefaults} width="100%" height="100%" {...props}>
      <path d="M3 10.5 12 4l9 6.5" />
      <path d="M5 9.5V20h14V9.5" />
    </svg>
  )
}

export function Browse(props: IconProps) {
  return (
    <svg {...strokeDefaults} width="100%" height="100%" {...props}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 13h8M8 16.5h5" />
    </svg>
  )
}

export function Apps(props: IconProps) {
  return (
    <svg {...strokeDefaults} width="100%" height="100%" {...props}>
      <path d="M8 3h8l4 4v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  )
}

export function Profile(props: IconProps) {
  return (
    <svg {...strokeDefaults} width="100%" height="100%" {...props}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4 3.5-6 8-6s8 2 8 6" />
    </svg>
  )
}

export function Dash(props: IconProps) {
  return (
    <svg {...strokeDefaults} width="100%" height="100%" {...props}>
      <rect x="3" y="3" width="8" height="8" rx="1.5" />
      <rect x="13" y="3" width="8" height="5" rx="1.5" />
      <rect x="13" y="10" width="8" height="11" rx="1.5" />
      <rect x="3" y="13" width="8" height="8" rx="1.5" />
    </svg>
  )
}

export function Post(props: IconProps) {
  return (
    <svg {...strokeDefaults} width="100%" height="100%" {...props}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

export function Applicants(props: IconProps) {
  return (
    <svg {...strokeDefaults} width="100%" height="100%" {...props}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20c0-3.3 2.7-5 6-5s6 1.7 6 5" />
      <path d="M16 4.5a3.2 3.2 0 0 1 0 6.4M18 15c2.4.5 3.5 2.3 3.5 5" />
    </svg>
  )
}

export function Export(props: IconProps) {
  return (
    <svg {...strokeDefaults} width="100%" height="100%" {...props}>
      <path d="M12 3v12M8 7l4-4 4 4" />
      <path d="M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4" />
    </svg>
  )
}

export function Matrix(props: IconProps) {
  return (
    <svg {...strokeDefaults} width="100%" height="100%" {...props}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
    </svg>
  )
}

export function Search(props: IconProps) {
  return (
    <svg {...strokeDefaults} width={17} height={17} {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3-3" />
    </svg>
  )
}

export function Check(props: IconProps) {
  return (
    <svg {...strokeDefaults} strokeWidth={2.4} width="100%" height="100%" {...props}>
      <path d="M5 12l4.5 4.5L19 7" />
    </svg>
  )
}

export function CheckSmall(props: IconProps) {
  return (
    <svg {...strokeDefaults} strokeWidth={3} width={11} height={11} {...props}>
      <path d="M5 12l4.5 4.5L19 7" />
    </svg>
  )
}

export function CheckCircle(props: IconProps) {
  return (
    <svg {...strokeDefaults} width="100%" height="100%" {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 12l2.5 2.5 4.5-5" />
    </svg>
  )
}

export function Lock(props: IconProps) {
  return (
    <svg {...strokeDefaults} width="100%" height="100%" {...props}>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  )
}
